import React, { useState, useEffect } from 'react';
import { getSearchableParamsGrouped } from '../../services/operations';
import './SearchFilters.css';

/**
 * Para operaciones con muchos parámetros repetidos, solo mostramos los útiles.
 * El resto de operaciones muestran todos sus parámetros searchable.
 */
const SIMPLIFIED_PARAMS = {
  'MECANIZAR': ['Diametro1', 'Longitud1'],
  'MECANIZAR_2FASE': ['D_maz_Cara'],
};

// Labels descriptivos y claros
const PARAM_LABELS = {
  // Mecanizado (simplificado)
  'Diametro1': 'Diámetro máximo',
  'Longitud1': 'Longitud total',
  // Mecanizar 2ª Fase (simplificado)
  'D_maz_Cara': 'Diámetro máx. cara',
  // Taladrado
  'Diametro': 'Diámetro agujero',
  'Profundidad': 'Profundidad',
  'Rosca_SiNo': 'Con rosca',
  'N_agujeros': 'Nº de agujeros',
  // Chavetero
  'Ancho': 'Ancho chavetero',
  'N_Chavetas': 'Nº de chavetas',
  // Tallado
  'Modulo': 'Módulo',
  'Diametro_Paso': 'Diámetro de paso',
  'Numero_Dientes': 'Nº de dientes',
};

const SearchFilters = ({ filters, onFilterChange, enabledOperations = [] }) => {
  const [operationsWithParams, setOperationsWithParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operationsExpanded, setOperationsExpanded] = useState(false);
  const [selectedOperations, setSelectedOperations] = useState(new Set());
  const [expandedOperations, setExpandedOperations] = useState(new Set());

  useEffect(() => {
    loadSearchableParams();
  }, []);

  const loadSearchableParams = async () => {
    try {
      const data = await getSearchableParamsGrouped();
      setOperationsWithParams(data.operations || []);
    } catch (err) {
      console.error('Error loading searchable params:', err);
    } finally {
      setLoading(false);
    }
  };

  const activeOperations = operationsWithParams.filter(op =>
    enabledOperations.length === 0 || enabledOperations.includes(op.id)
  );

  const toggleOperationSelection = (opId) => {
    const newSelected = new Set(selectedOperations);
    const newExpanded = new Set(expandedOperations);
    if (newSelected.has(opId)) {
      newSelected.delete(opId);
      newExpanded.delete(opId);
    } else {
      newSelected.add(opId);
      newExpanded.add(opId); // Expandir al seleccionar
    }
    setSelectedOperations(newSelected);
    setExpandedOperations(newExpanded);
  };

  const toggleExpanded = (opId) => {
    const newExpanded = new Set(expandedOperations);
    if (newExpanded.has(opId)) {
      newExpanded.delete(opId);
    } else {
      newExpanded.add(opId);
    }
    setExpandedOperations(newExpanded);
  };

  // Para operaciones simplificadas, filtrar solo los params útiles. Resto: todos.
  const getParamsToShow = (operation) => {
    const simplifiedKeys = SIMPLIFIED_PARAMS[operation.key];
    if (simplifiedKeys) {
      return operation.params.filter(p => simplifiedKeys.includes(p.param_key));
    }
    return operation.params;
  };

  const getParamLabel = (param) => {
    return PARAM_LABELS[param.param_key] || param.param_name;
  };

  const renderFilter = (param) => {
    const { param_key, data_type } = param;
    const label = getParamLabel(param);

    if (data_type === 'boolean') {
      return (
        <div key={param_key} className="filter-field">
          <label>{label}</label>
          <select
            value={filters[param_key] === true ? 'true' : filters[param_key] === false ? 'false' : ''}
            onChange={(e) => onFilterChange(param_key, e.target.value === '' ? null : e.target.value === 'true')}
          >
            <option value="">Cualquiera</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        </div>
      );
    }

    if (data_type === 'decimal' || data_type === 'integer') {
      return (
        <div key={param_key} className="filter-field filter-range">
          <label>{label}</label>
          <div className="range-inputs">
            <input
              type="number"
              placeholder="Mín"
              value={filters[`${param_key}_min`] || ''}
              onChange={(e) => onFilterChange(`${param_key}_min`, e.target.value ? parseFloat(e.target.value) : null)}
              step={data_type === 'decimal' ? '0.01' : '1'}
            />
            <span className="range-to">a</span>
            <input
              type="number"
              placeholder="Máx"
              value={filters[`${param_key}_max`] || ''}
              onChange={(e) => onFilterChange(`${param_key}_max`, e.target.value ? parseFloat(e.target.value) : null)}
              step={data_type === 'decimal' ? '0.01' : '1'}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="search-filters">
      <h3>Filtros de búsqueda</h3>

      <div className="filter-row">
        <div className="filter-field">
          <label>Tipo de pieza</label>
          <select
            value={filters.part_type || ''}
            onChange={(e) => onFilterChange('part_type', e.target.value || null)}
          >
            <option value="">Todos los tipos</option>
            <option value="eje">Eje</option>
            <option value="engranaje">Engranaje</option>
            <option value="piñon">Piñón</option>
            <option value="corona">Corona</option>
            <option value="otro">Otro</option>
          </select>
        </div>

        <div className="filter-field">
          <label>Nº resultados</label>
          <select
            value={filters.limit || 10}
            onChange={(e) => onFilterChange('limit', parseInt(e.target.value))}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {!loading && activeOperations.length > 0 && (
        <div className="operations-filter-wrapper">
          <div className="operations-filter-header" onClick={() => setOperationsExpanded(!operationsExpanded)}>
            <div className="operations-header-left">
              <h4>Filtros por operación</h4>
              <span className="operations-summary">
                {selectedOperations.size === 0
                  ? 'Sin filtros'
                  : `${selectedOperations.size} operación${selectedOperations.size > 1 ? 'es' : ''}`}
              </span>
            </div>
            <span className="expand-icon">{operationsExpanded ? '▲' : '▼'}</span>
          </div>

          {operationsExpanded && (
            <div className="operations-filter-content">
              <p className="selector-hint">Selecciona operaciones para añadir filtros específicos. Deja vacío para no filtrar.</p>
              <div className="operations-chips">
                {activeOperations.map(op => (
                  <button
                    key={op.id}
                    type="button"
                    className={`operation-chip ${selectedOperations.has(op.id) ? 'selected' : ''}`}
                    onClick={() => toggleOperationSelection(op.id)}
                  >
                    {op.name}
                  </button>
                ))}
              </div>

              {selectedOperations.size > 0 && (
                <div className="selected-operations-filters">
                  {activeOperations
                    .filter(op => selectedOperations.has(op.id))
                    .map(operation => {
                      const paramsToShow = getParamsToShow(operation);
                      if (paramsToShow.length === 0) return null;
                      const isExpanded = expandedOperations.has(operation.id);

                      return (
                        <div key={operation.id} className="operation-filter-panel">
                          <div
                            className="operation-filter-panel-header"
                            onClick={() => toggleExpanded(operation.id)}
                          >
                            <span className="operation-name">{operation.name}</span>
                            <div className="panel-header-actions">
                              <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                              <button
                                type="button"
                                className="btn-remove-filter"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleOperationSelection(operation.id);
                                }}
                                title="Quitar filtro"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="operation-filter-panel-content">
                              {paramsToShow.map(param => renderFilter(param))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
